import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { type ActiveLocationGroup, isOnStageMember } from "./index";

export interface OnStageLayoutAdjust {
  anchorX: number;
  topYDelta: number;
}

/** On-stage stack: primary X, each active part stepped down. */
export function onStageLayoutAdjustments(
  groups: readonly ActiveLocationGroup[],
  stageByRunId: ReadonlyMap<string, { anchorX: number; topY: number }>,
  layout: BookTimelineLayout,
  playheadMs: number,
): Map<string, OnStageLayoutAdjust> {
  const out = new Map<string, OnStageLayoutAdjust>();
  const cohortCountByBranch = new Map<string, number>();
  for (const group of groups) {
    cohortCountByBranch.set(
      group.branchId,
      (cohortCountByBranch.get(group.branchId) ?? 0) + 1,
    );
  }
  for (const group of groups) {
    const primary = group.members.find(
      (member) => member.role === "primary" && member.visible,
    );
    if (primary === undefined) {
      continue;
    }
    const primaryStage = stageByRunId.get(primary.runId);
    if (primaryStage === undefined) {
      continue;
    }
    const onStage = group.members
      .filter((member) => isOnStageMember(member, group, layout, playheadMs))
      .sort((a, b) => {
        if (a.role === "primary") {
          return -1;
        }
        if (b.role === "primary") {
          return 1;
        }
        return 0;
      });
    const columnAnchor = (() => {
      const secondary = onStage.find((member) => member.role === "secondary");
      if (secondary !== undefined) {
        const secondaryStage = stageByRunId.get(secondary.runId);
        if (secondaryStage !== undefined) {
          return secondaryStage.anchorX;
        }
      }
      return primaryStage.anchorX;
    })();
    const splitBranch = (cohortCountByBranch.get(group.branchId) ?? 0) > 1;
    for (const member of onStage) {
      const memberStage = stageByRunId.get(member.runId);
      let anchorX = columnAnchor;
      if (member.role === "primary") {
        anchorX = splitBranch ? primaryStage.anchorX : columnAnchor;
      } else {
        anchorX = memberStage?.anchorX ?? columnAnchor;
      }
      out.set(member.runId, {
        anchorX,
        // Cast-column Y comes from placement plan (primary vs secondary roles).
        topYDelta: 0,
      });
    }
  }
  return out;
}
